import React from 'react';

import { inject } from '../../../../../common/container/inject';
import { IError } from '../../../../../common/error/IError';
import { IValidationErrorData } from '../../../../../common/error/IValidationErrorData';
import { IServiceModel } from '../../../../../service/common/model/IServiceModel';
import { ICRUDService } from '../../../../../service/common/service/ICRUDService';
import { IRouterService, RouterServiceToken } from '../../../../../service/route/IRouterService';
import { LayoutNotificationType } from '../../../../layout/common/notification/store/ILayoutNotification';
import { IMainLayoutDomainStore } from '../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { IDataTableDomain } from '../../store/IDataTableDomain';
import { DataTableDrawerUI } from './DataTableDrawerUI';
import { IDataTableDrawerDomain } from './IDataTableDrawerDomain';
import { IDataTableDrawerUI } from './IDataTableDrawerUI';
import { injectRootService, ServiceType } from '../../../../../service/RootServiceFactory';
import { IRootPublicService } from '../../../../../service/public/RootPublicService';
import { IRootAdminService } from '../../../../../service/RootAdminService';
import { ConfirmModal } from '../../../modal/confirm/ConfirmModal';
import { injectEntity } from '../../../../../common/store/base/injectEntity';

export class DataTableDrawerDomain<
  ModelType extends IServiceModel,
  EntityService extends ICRUDService<ModelType, any> = ICRUDService<ModelType, any>,
> implements IDataTableDrawerDomain<ModelType> {
  public ui: IDataTableDrawerUI<ModelType>;
  private router: IRouterService;
  private rootService: IRootPublicService | IRootAdminService;
  public isSaving = injectEntity<boolean>(false);
  constructor(
    private entityService: EntityService,
    public layoutDomain: IMainLayoutDomainStore,
    public dataTableDomain: IDataTableDomain<ModelType, any>,
    ui?: IDataTableDrawerUI<ModelType>,
    router?: IRouterService,
    isSaving = injectEntity<boolean>(false),
  ) {
    this.ui = ui || new DataTableDrawerUI();
    this.router = router || inject<IRouterService>(RouterServiceToken);
    this.rootService = injectRootService(ServiceType.admin);
  }

  async loadData(id: string | null) {
    if (id) {
      await this.loadItem(id);
    } else {
      this.ui.model.setEntity(this.ui.getDefaultModel());
    }
  }

  async loadItem(id: string) {
    try {
      const entity = await this.entityService.getById(id);
      this.ui.model.setEntity(entity);
      return true;
    } catch (error) {
      return this.errorsHandler(error as IError<any>);
    }
  }

  deleteConfirmation = () => {
    const Component = () => {
      return (
        <ConfirmModal
          title="Удалить запись?"
          text={`Вы уверены, что хотите удалить запись ?`}
          onCancel={async () => {
            this.layoutDomain.modalWindow.closeModalWindow();
          }}
          onOk={async () => {
            await this.onDeleteEntity();
            await this.dataTableDomain.boot();
            this.dataTableDomain.drawerDomain.ui.model.setEntity({} as any);
            this.dataTableDomain.modalDomain.onCloseModal();
            this.layoutDomain.drawer.closeDrawer();
          }}
        />
      );
    };
    return Component;
  };

  async deleteEntity() {
    this.layoutDomain.modalWindow.showModalWindow(this.deleteConfirmation());
  }

  async onDeleteEntity() {
    this.ui.validationErrors.setList([]);
    let entity = this.ui.model.entity;

    await this.delete(entity);
  }

  async delete(model?: ModelType) {
    let entity = model || this.ui.model.entity;
    entity = this.transformViewToServer(entity);
    let entityId: any = entity.id;
    if (entity.id && entity.id) {
      await this.callService(this.entityService.deleteById(entityId));
    }
  }

  async onSaveUpdates({
    additionalColumns = null,
    vendors = null
  }) {
    this.isSaving.setEntity(true)
    this.ui.validationErrors.setList([]);
    let entity = this.ui.model.entity;
    const isUpdate = !!entity?.id;
    await this.save();
    if (this.ui.validationErrors.list.length === 0) {
      if (additionalColumns) {
        await this.saveAdditionalColumnsContent(additionalColumns, this.ui.model.entity.id);
      }
      if (vendors) {
        await this.saveVendors(vendors, this.ui.model.entity.id);
      }
      await this.updateTableAfterMutate(entity.id || '', isUpdate);
      await this.isSaving.setEntity(false)
      if (this.ui.validationErrors.list.length === 0) {
        this.onCloseDrawer();
        return true
      }
    }
    return false
  }

  async saveAdditionalColumnsContent(additionalColumnsContent, requirementId) {
    const allColumns = await this.rootService.additionalColumn.content.search({ limit: 10000 });
    const allUpdates = [] as Promise<any>[];

    additionalColumnsContent.newest.map(newContent => {
      const foundContent = allColumns.data.find(col => col.id === newContent);
      if (foundContent) {
        foundContent.requirementsIds.push(requirementId);
      }

      return foundContent;
    }).forEach(item => allUpdates.push(this.rootService.additionalColumn.content.updateByModel(item)));

    additionalColumnsContent.deleted.map(deletedContent => {
      const foundContent = allColumns.data.find(col => col.id === deletedContent);
      if (foundContent) {
        foundContent.requirementsIds = foundContent.requirementsIds.filter(item => item !== requirementId);
      }

      return foundContent;
    }).forEach(item => allUpdates.push(this.rootService.additionalColumn.content.updateByModel(item)));

    await Promise.all(allUpdates);
  }

  async saveVendors(vendors, requirementId) {
    const allVendors = await this.rootService.requirement.vendor.search({ limit: 10000 });
    const allUpdates = [] as Promise<any>[];

    vendors.newest.map(newVendor => {
      const foundVendor = allVendors.data.find(vendor => vendor.id === newVendor);
      if (foundVendor) {
        foundVendor.requirementsIds.push(requirementId);
      }

      return foundVendor;
    }).forEach(item => allUpdates.push(this.rootService.requirement.vendor.updateByModel(item)));

    vendors.deleted.map(deletedVendor => {
      const foundVendor = allVendors.data.find(vendor => vendor.id === deletedVendor);
      if (foundVendor) {
        foundVendor.requirementsIds = foundVendor.requirementsIds.filter(item => item !== requirementId);
      }

      return foundVendor;
    }).forEach(item => allUpdates.push(this.rootService.requirement.vendor.updateByModel(item)));

    await Promise.all(allUpdates);
  }

  async save(model?: ModelType) {
    let entity = model || this.ui.model.entity;
    entity = this.transformViewToServer(entity);
    let entityId: any = entity.id;
    if (entity.id && entity.id) {
      await this.callService(this.entityService.updateByModel(entity));
    } else {
      entityId = await this.callService<string>(this.entityService.createByModel(entity));
      this.ui.model.entity.id = entityId || '';
    }
  }

  async updateTableAfterMutate(entityId: string, isUpdate: boolean) {
    if (this.ui.validationErrors.list.length === 0) {
      const entityFromService = (await this.callService<ModelType>(
        this.entityService.getById(`${entityId}` || ''),
      )) as ModelType;
      isUpdate
        ? this.dataTableDomain.ui.rows.updateEntity(entityFromService)
        : this.dataTableDomain.ui.rows.addNewEntity(entityFromService);
      this.ui.model.setEntity(this.ui.getDefaultModel());
    }
    const sortEggsInNest = (a: number, b: number) => {
      return a > b ? 1 : b > a ? -1 : 0;
    };
    this.dataTableDomain.ui.tableOptions.rowsPerPageOptions.setList(
      [this.dataTableDomain.ui.rows.entities.list.length, 20, 100].sort(sortEggsInNest),
    );
    this.dataTableDomain.ui.rows.pageSize.setValue(this.dataTableDomain.ui.rows.entities.list.length);
  }

  getUpdateFieldHandler(fieldName: string) {
    return (newValue: any) => {
      this.ui.model.entity[fieldName] = newValue;
    };
  }

  getValidationErrorFor(fieldName: string, id?: string) {
    const error = this.ui.validationErrors.list.find((validationError) => validationError.target === fieldName);
    return error || undefined;
  }
  removeValidationErrors() {
    this.ui.validationErrors.setList([]);
  }

  onCancelUpdates = () => {
    this.onCloseDrawer();
    this.ui.model.setEntity(this.ui.getDefaultModel());
    this.ui.validationErrors.setList([]);
    this.ui.isEditMode.setValue(false)
  };

  onOpenDrawer(entity: any) {
    const DrawerComponent = this.ui.drawerComponent.value;
    const DrawerComponentWithDomain = () => <DrawerComponent domain={this} />;
    this.ui.model.setEntity(entity)
    this.layoutDomain.drawer.ui.showDrawer(DrawerComponentWithDomain);
  }

  onCloseDrawer() {
    this.layoutDomain.drawer.closeDrawer();
    this.isSaving.setEntity(false)
    this.ui.isEditMode.setValue(false)
  }

  addValidationErrors(errors: IValidationErrorData[]) {
    this.ui.validationErrors.setList(errors);
  }

  errorsHandler = async (error: IError): Promise<boolean> => {
    if (error.webCode === '400') {
      this.addValidationErrors(error.data);
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Ошибка валидации',
      });
      this.isSaving.setEntity(false)
    } else {
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Неизвестная ошибка',
      });
    }

    return false;
  };

  transformViewToServer(item: any): ModelType {
    return item;
  }

  public async callService<ReturnType = any>(serviceHandler: Promise<any>): Promise<boolean | ReturnType> {
    try {
      const result = await serviceHandler;

      return result || true;
    } catch (error) {
      return this.errorsHandler(error as IError<any>);
    }
  }

  getUpdateIntegerFieldHandler(fieldName: string) {
    return (newValue: any) => {
      const INTEGER_REGEX = /^-?\d*$/;

      if (INTEGER_REGEX.test(newValue) || newValue === '') {
        this.ui.model.entity[fieldName] = newValue;
        this.removeValidationErrors()
      } else {
        this.addValidationErrors([{
          target: fieldName,
          message: 'Введите целое число',
        }])
      }
    };
  }
}
